import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import type { SharedMarketingProps } from '@/types/marketing'

interface AboutPageProps extends SharedMarketingProps {
  hero: {
    eyebrow: string
    title: string
    body: string
  }
  story: { year: string; title: string; body: string }[]
  values: { title: string; body: string }[]
  builders: {
    eyebrow: string
    title: string
    body_before: string
    partner: { name: string; url: string }
    body_after: string
    link: { label: string; href: string }
  }
  products: {
    eyebrow: string
    title: string
    body: string
    items: {
      name: string
      logo_url: string
      // NOT A BADGE FOR DECORATION. It mirrors `TechnologyContent::MODULES`,
      // where status is a promise — `live` means a customer could use it this
      // week. lnkbee carries `live` since 2026-09-14. The label says what is
      // true, and changes only when that entry does.
      status: string
      body: string
      domain: { label: string; url: string }
      // Absent while the technology section is unlisted (Settings → System).
      link?: { label: string; href: string }
    }[]
  }
  cta: {
    title: string
    body: string
    actions: { label: string; href: string }[]
  }
}

export default function About(props: AboutPageProps) {
  return (
    <>
      <AtmosphericHero minHeight="56vh" containerClassName="hero-content">
        <div className="hero-text">
          <span className="hero-eyebrow">{props.hero.eyebrow}</span>
          <h1 className="hero-headline" style={{ fontSize: 'clamp(2.5rem, 7vw, 6rem)' }}>{props.hero.title}</h1>
          <p className="hero-body">{props.hero.body}</p>
        </div>
      </AtmosphericHero>

      {/* Story — moved to cream so the page alternates D-C-D-C. Each
          entry is a marketing-light-card with year + title + body, full
          container width grid. */}
      <section className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">Our story</div>
              <h2 className="marketing-section-title marketing-section-title-dark">How wavbee got here.</h2>
            </div>
          </ScrollReveal>

          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {props.story.map((item, index) => (
              <ScrollReveal key={item.year} direction="up" delay={index * 0.08}>
                <div className="marketing-light-card h-full">
                  <div className="marketing-eyebrow-mono">{item.year}</div>
                  <h3 className="mt-3 text-xl font-semibold text-[#151515]">{item.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-[#3b3b3b]">{item.body}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values — moved to dark with a cool glow. Reads as the brand's
          'what we stand for' beat after the warm story zone above. */}
      <section className="marketing-section has-glow">
        <div className="section-glow cool" style={{ width: '40rem', height: '40rem', left: '-12rem', top: '-12rem' }} />
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">What we stand for</div>
              <h2 className="marketing-section-title">What we won't compromise on.</h2>
            </div>
          </ScrollReveal>

          <div className="grid gap-5 md:grid-cols-2">
            {props.values.map((value, index) => (
              <ScrollReveal key={value.title} direction="up" delay={index * 0.1}>
                <div className="marketing-surface-card h-full">
                  <h3 className="text-xl font-semibold text-white">{value.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-white/72">{value.body}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHO BUILDS IT. Between the values and the CTA on purpose: after a
          reader knows what we stand for, and before they are asked to apply.
          Saying the software is built with a partner is more honest than
          letting the page imply one team does distribution and engineering
          both. */}
      <section className="marketing-section">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-surface-card mx-auto max-w-3xl">
              <div className="marketing-eyebrow-mono">{props.builders.eyebrow}</div>
              <h2 className="mt-3 text-2xl font-semibold text-white">{props.builders.title}</h2>
              <p className="mt-4 text-sm leading-7 text-white/72">
                {props.builders.body_before}
                <a
                  href={props.builders.partner.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#ff6a1a] hover:underline"
                >
                  {props.builders.partner.name}
                </a>
                {props.builders.body_after}
              </p>
              <a
                href={props.builders.link.href}
                className="mt-5 inline-block text-sm text-[#ff6a1a] hover:underline"
              >
                {props.builders.link.label} &rarr;
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Products of wavbee's own, after the story and before the CTA: a reader
          has just learnt who we are, which is when "and here is what we build"
          lands. Warm variant to keep the page's alternating rhythm. */}
      <section className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="mx-auto max-w-3xl">
              <div className="marketing-eyebrow-mono">{props.products.eyebrow}</div>
              <h2 className="marketing-section-title marketing-section-title-dark">{props.products.title}</h2>
              <p className="mt-4 text-sm leading-7 text-black/70">{props.products.body}</p>
            </div>

            <div className="mx-auto mt-8 max-w-3xl space-y-5">
              {props.products.items.map(item => (
                <div key={item.name} className="marketing-surface-card">
                  <div className="flex items-start gap-4">
                    <img
                      src={item.logo_url}
                      alt=""
                      aria-hidden="true"
                      width={48}
                      height={48}
                      className="h-12 w-12 shrink-0 rounded-xl"
                    />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <h3 className="text-lg font-semibold text-white">{item.name}</h3>
                        <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-white/60">
                          {item.status}
                        </span>
                      </div>
                      <p className="mt-3 text-sm leading-7 text-white/72">{item.body}</p>
                      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
                        <a
                          href={item.domain.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-[#ff6a1a] hover:underline"
                        >
                          {item.domain.label} &rarr;
                        </a>
                        {item.link && (
                          <a href={item.link.href} className="text-sm text-white/60 hover:text-white hover:underline">
                            {item.link.label}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Final CTA — cream variant, page ends C. */}
      <section className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-final-cta">
              <div>
                <div className="marketing-eyebrow-mono">Get started</div>
                <h2 className="marketing-section-title">{props.cta.title}</h2>
                <p className="marketing-lead max-w-2xl">{props.cta.body}</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                {props.cta.actions.map((action, index) => (
                  <a key={action.href} href={action.href}
                    className={index === 0 ? 'marketing-button-primary' : 'marketing-button-secondary'}>
                    {action.label}
                  </a>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </>
  )
}
